import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Popover } from "../Popover/Popover.js";
/* empty css              */
import { useEffect, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/_dateField/DateField.tsx
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
/**
* The field shared by DatePicker and DateRangePicker: a button drawn like Input that opens
* a popover of calendars, with a clear button over its end. On open, focus moves to the
* calendar's tab stop once the panel is placed; Down Arrow opens it too.
*/
function DateField({ id, size, invalid, disabled, valueText, placeholder, describedBy, "aria-label": ariaLabel, clearLabel, onClear, open, onOpenChange, popoverLabel, children, triggerRef, className, typed }) {
	const valueId = `${id}-value`;
	const buttonRef = useRef(null);
	const inputRef = useRef(null);
	const panelRef = useRef(null);
	const [draft, setDraft] = useState(null);
	const [typedInvalid, setTypedInvalid] = useState(false);
	const typedText = typed?.text;
	const [textShown, setTextShown] = useState(typedText);
	if (textShown !== typedText) {
		setTextShown(typedText);
		setDraft(null);
		setTypedInvalid(false);
	}
	const commitDraft = () => {
		if (!typed || draft === null) return true;
		const taken = typed.onCommit(draft);
		if (taken) setDraft(null);
		setTypedInvalid(!taken);
		return taken;
	};
	useEffect(() => {
		if (!open) return;
		let frame = 0;
		let tries = 0;
		const focusDay = () => {
			const day = panelRef.current?.querySelector("[role=\"grid\"] [tabindex=\"0\"]");
			const placed = day && getComputedStyle(day).visibility !== "hidden";
			if (day && placed) day.focus();
			else if (tries++ < 20) frame = requestAnimationFrame(focusDay);
		};
		frame = requestAnimationFrame(focusDay);
		return () => cancelAnimationFrame(frame);
	}, [open]);
	const hintId = `${id}-format`;
	const trigger = typed ? /* @__PURE__ */ jsx("button", {
		ref: (element) => {
			buttonRef.current = element;
			setRef(triggerRef, element);
		},
		type: "button",
		disabled,
		"aria-label": typed.calendarLabel,
		className: "ds-datepicker__calendar",
		children: /* @__PURE__ */ jsx(Icon, {
			name: "calendar",
			className: "ds-datepicker__icon"
		})
	}) : /* @__PURE__ */ jsxs("button", {
		ref: (element) => {
			buttonRef.current = element;
			setRef(triggerRef, element);
		},
		type: "button",
		id,
		disabled,
		"aria-label": ariaLabel,
		"aria-describedby": cx(valueId, describedBy),
		className: "ds-datepicker__trigger",
		onKeyDown: (event) => {
			if (event.key === "ArrowDown" && !open) {
				event.preventDefault();
				onOpenChange(true);
			}
		},
		children: [/* @__PURE__ */ jsx(Icon, {
			name: "calendar",
			className: "ds-datepicker__icon"
		}), /* @__PURE__ */ jsx("span", {
			id: valueId,
			className: cx("ds-datepicker__value", valueText === null && "ds-datepicker__value--placeholder"),
			children: valueText ?? placeholder
		})]
	});
	const showClear = clearLabel !== null && !disabled;
	const popover = /* @__PURE__ */ jsx(Popover, {
		trigger,
		open: open && !disabled,
		onOpenChange,
		placement: typed ? "bottom-end" : "bottom-start",
		"aria-label": popoverLabel,
		children: /* @__PURE__ */ jsx("div", {
			ref: panelRef,
			className: "ds-datepicker__panel",
			children
		})
	});
	const clear = showClear && /* @__PURE__ */ jsx(IconButton, {
		icon: "x",
		size: "sm",
		"aria-label": clearLabel,
		className: "ds-datepicker__clear",
		onClick: () => {
			onClear();
			(typed ? inputRef : buttonRef).current?.focus();
		}
	});
	return /* @__PURE__ */ jsx("div", {
		className: cx("ds-datepicker", `ds-datepicker--${size}`, (invalid || typedInvalid) && "ds-datepicker--invalid", disabled && "ds-datepicker--disabled", showClear && "ds-datepicker--clearable", typed && "ds-datepicker--typed", className),
		children: typed ? /* @__PURE__ */ jsxs(Fragment$1, { children: [
			/* @__PURE__ */ jsx("input", {
				ref: inputRef,
				id,
				type: "text",
				inputMode: "numeric",
				autoComplete: "off",
				disabled,
				"aria-label": ariaLabel,
				"aria-invalid": invalid || typedInvalid || void 0,
				"aria-describedby": cx(hintId, describedBy),
				placeholder: typed.placeholder,
				size: typed.size,
				value: draft ?? typed.text,
				className: "ds-datepicker__trigger ds-datepicker__input",
				onChange: (event) => setDraft(event.target.value),
				onBlur: commitDraft,
				onKeyDown: (event) => {
					if (event.key === "Enter") {
						if (!commitDraft()) event.preventDefault();
					} else if (event.key === "Escape" && draft !== null) {
						event.preventDefault();
						setDraft(null);
						setTypedInvalid(false);
					} else if (event.key === "ArrowDown" && event.altKey && !open) {
						event.preventDefault();
						if (commitDraft()) onOpenChange(true);
					}
				}
			}),
			/* @__PURE__ */ jsx(VisuallyHidden, {
				id: hintId,
				children: typed.hint
			}),
			clear,
			popover
		] }) : /* @__PURE__ */ jsxs(Fragment$1, { children: [popover, clear] })
	});
}
//#endregion
export { DateField };

//# sourceMappingURL=DateField.js.map